import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'dmsp-theme';

const read = (): Theme => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') return stored;
    } catch {
        /* storage unavailable — fall through to the media query */
    }
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
};

const apply = (theme: Theme) => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    // shadcn/ui primitives key off the `.dark` class; keep both in sync.
    root.classList.toggle('dark', theme === 'dark');
};

/** Light/dark preference, persisted and reflected on <html> for the CSS tokens. */
export const useTheme = () => {
    const [theme, setTheme] = useState<Theme>(read);

    useEffect(() => {
        apply(theme);
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            /* ignore — the theme still applies for this session */
        }
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
    }, []);

    return { theme, setTheme, toggleTheme };
};
